import React, {useState} from 'react';
import {
    Box,
    Button,
    FormControl,
    FormHelperText,
    IconButton,
    InputAdornment,
    InputLabel,
    Link,
    OutlinedInput,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {useNavigate} from 'react-router-dom';

import imgLogin from '../../img/v2/login.svg';
import {IAuthRequest} from '../../interfaces/fetch/AuthRequest';
import instance from '../../instance';
import {Visibility, VisibilityOff} from '../Icons/icons';
import ForgotPassword from '../ForgotPassword/ForgotPassword';
import {getLabelWithAsterisk} from '../../functionts/getLabelWithAsterisk';
import styles from './LoginPage.module.scss';

interface IUserIdProps {
    setUserId: React.Dispatch<React.SetStateAction<number | null>>;
    setAuth: React.Dispatch<React.SetStateAction<boolean>>;
    setAdmin: React.Dispatch<React.SetStateAction<boolean>>;
    setSuperAdmin: React.Dispatch<React.SetStateAction<boolean>>;
    setInn: React.Dispatch<React.SetStateAction<string | null>>;
    isCookieError: boolean;
    setCookieError: React.Dispatch<React.SetStateAction<boolean>>;
}

const LoginPage = (props: IUserIdProps): React.JSX.Element => {
    const {setUserId, setAuth, setAdmin, setSuperAdmin, setInn} = props;
    const [forgotPasswordShow, setForgotPasswordShow] = useState(false);

    const navigate = useNavigate();

    const [loginError, setLoginError] = useState(false);

    const [values, setValues] = useState({
        login: '',
        password: '',
        showPassword: false,
    });

    const [touched, setTouched] = useState({
        login: false,
        password: false,
    });

    const isLoginValid = /^\d{12}$/.test(values.login);
    const isPasswordValid = values.password.trim().length > 0;
    const isFormValid = isLoginValid && isPasswordValid;

    const handleChange = (prop: keyof typeof values) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setLoginError(false);

        let inputValue = event.target.value;

        if (prop === 'login') {
            inputValue = inputValue.replace(/\D/g, '').slice(0, 12);
        }

        setValues({...values, [prop]: inputValue});
    };

    const handleBlur = (field: keyof typeof touched) => {
        setLoginError(false);
        setTouched({...touched, [field]: true});
    };

    const handleClickShowPassword = () => {
        setValues({...values, showPassword: !values.showPassword});
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setTouched({login: true, password: true});

        if (!isFormValid) return;

        const authRequest: IAuthRequest = {
            method: 'Auth',
            params: {
                inn: values.login,
                password: values.password,
            },
        };

        try {
            const response = await instance.post('/LK', authRequest);
            if (!response.data.error && response.data.access) {
                setUserId(response.data.user_id);
                setAuth(true);
                setAdmin(response.data.is_admin);
                setSuperAdmin(response.data.is_super_admin);
                setInn(String(response.data.inn));
                sessionStorage.setItem('isAuth', 'true');
                sessionStorage.setItem('inn', String(response.data.inn));
                sessionStorage.setItem('userInfo', JSON.stringify(response.data));
                sessionStorage.setItem('isAdmin', response.data.is_admin ? 'true' : 'false');
                sessionStorage.setItem('isSuperAdmin', response.data.is_super_admin ? 'true' : 'false');
                if (response.data.is_super_admin) {
                    navigate('/admin');
                } else if (response.data.is_admin) {
                    navigate('/kkm-control');
                } else {
                    navigate('/home');
                }
            } else if (response.data.access === false) {
                setLoginError(true);
            } else {
                setTouched(prev => ({...prev, password: true}));
            }
        } catch (error) {
            // можно логировать или вызвать уведомление
        }
    };

    return (
        <>
            <Box className={styles.container}>
                <Paper elevation={0} className={styles.paper}>
                    <img src={imgLogin} alt="Login" />
                    <form onSubmit={e => void handleSubmit(e)} autoComplete="off" className={styles.form}>
                        <Stack spacing={2}>
                            <FormControl fullWidth error={(touched.login && !isLoginValid) || loginError}>
                                <TextField
                                    label={getLabelWithAsterisk('ИИН/БИН', true)}
                                    type="text"
                                    value={values.login}
                                    onChange={handleChange('login')}
                                    onBlur={() => handleBlur('login')}
                                    inputProps={{maxLength: 12}}
                                    autoComplete="new-login"
                                    error={(touched.login && !isLoginValid) || loginError}
                                />
                                {touched.login && !isLoginValid && <FormHelperText>Введите корректный ИИН/БИН, 12 цифр</FormHelperText>}
                            </FormControl>

                            <FormControl fullWidth error={(touched.password && !isPasswordValid) || loginError}>
                                <InputLabel htmlFor="password">{getLabelWithAsterisk('Пароль', true)}</InputLabel>
                                <OutlinedInput
                                    id="password"
                                    type={values.showPassword ? 'text' : 'password'}
                                    value={values.password}
                                    onChange={handleChange('password')}
                                    onBlur={() => handleBlur('password')}
                                    autoComplete="new-password"
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">
                                                {values.showPassword ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    label={getLabelWithAsterisk('Пароль', true)}
                                />
                                {touched.password && !isPasswordValid && <FormHelperText>Пароль не может быть пустым</FormHelperText>}
                                {loginError && <FormHelperText>ИИН/БИН или пароль указаны неверно</FormHelperText>}
                            </FormControl>

                            <Tooltip
                                title="Заполните корректные данные"
                                disableHoverListener={isFormValid}
                                disableFocusListener={isFormValid}
                                disableTouchListener={isFormValid}
                            >
                                <span>
                                    <Button type="submit" variant="contained" color="primary" fullWidth disabled={!isFormValid}>
                                        Войти
                                    </Button>
                                </span>
                            </Tooltip>
                        </Stack>
                    </form>
                </Paper>

                <Box className={styles.links}>
                    <Link onClick={() => void navigate('/merchantinfo')} className={styles.linksItem}>
                        Как зарегистрироваться?
                    </Link>
                    <Box className={styles.separator} />
                    <Link onClick={() => setForgotPasswordShow(true)} className={styles.linksItem}>
                        Забыли пароль?
                    </Link>
                </Box>

                <Typography variant="body2" className={styles.info}>
                    АПК Портал<br /><a href={'/offer'} target={'_blank'} style={{color: '#fff'}}>Договор-оферта</a>
                </Typography>
            </Box>

            <ForgotPassword isShow={forgotPasswordShow} setShow={setForgotPasswordShow} />
        </>
    );
};

export default LoginPage;
